import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Checkbox,
  Input,
  Button,
  Card,
  Row,
  Col,
  Badge,
  Space,
  Typography,
  Divider,
  message,
  Spin,
  Collapse,
  Tag,
  Tooltip,
  Empty,
  Form,
  DatePicker
} from 'antd';
import {
  SearchOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  SelectOutlined,
  UnorderedListOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { predefinedLabOrders, getAllPredefinedOrders, searchOrders } from '../configs/predefinedLabOrders';
import { useAuth } from '../context/AuthContext';
import MomentDatePicker from './MomentDatePicker';
import moment from 'moment';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Search } = Input;

const PredefinedLabOrdersModal = ({
  visible,
  onClose,
  onOrdersSelected,
  patientId,
  title = "Select Laboratory Orders"
}) => {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(['hematology', 'biochemistry']);
  const [language, setLanguage] = useState('en'); // 'en' or 'vi'
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment().add(30, 'days'));
  const [form] = Form.useForm();
  const { user } = useAuth();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible) {
      setSelectedOrders([]);
      setSearchTerm('');
      setExpandedCategories(['hematology', 'biochemistry']);
      // Reset dates to defaults
      const defaultStartDate = moment();
      const defaultEndDate = moment().add(30, 'days');
      setStartDate(defaultStartDate);
      setEndDate(defaultEndDate);
      form.setFieldsValue({
        startDate: defaultStartDate,
        endDate: defaultEndDate
      });
    }
  }, [visible, form]);

  // Filter orders based on search term
  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) {
      return predefinedLabOrders;
    }
    
    const searchResults = searchOrders(searchTerm, language);
    const groupedResults = {};
    
    searchResults.forEach(order => {
      const categoryId = order.categoryId;
      if (!groupedResults[categoryId]) {
        const category = predefinedLabOrders[categoryId];
        groupedResults[categoryId] = {
          ...category,
          orders: []
        };
      }
      groupedResults[categoryId].orders.push(order);
    });
    
    return groupedResults;
  }, [searchTerm, language]);

  // Handle individual order selection
  const handleOrderSelect = (order, checked) => {
    if (checked) {
      setSelectedOrders(prev => [...prev, order]);
    } else {
      setSelectedOrders(prev => prev.filter(o => o.id !== order.id));
    }
  };

  // Handle category selection (select/deselect all in category)
  const handleCategorySelect = (categoryOrders, checked) => {
    if (checked) {
      const newOrders = categoryOrders.filter(order => 
        !selectedOrders.some(selected => selected.id === order.id)
      );
      setSelectedOrders(prev => [...prev, ...newOrders]);
    } else {
      const categoryOrderIds = categoryOrders.map(order => order.id);
      setSelectedOrders(prev => prev.filter(order => 
        !categoryOrderIds.includes(order.id)
      ));
    }
  };

  // Clear all selections
  const handleClearAll = () => {
    const clearedCount = selectedOrders.length;
    setSelectedOrders([]);
    if (clearedCount > 0) {
      message.info(`Cleared ${clearedCount} selected orders.`);
    }
  };

  // Select all visible orders
  const handleSelectAll = () => {
    const allVisibleOrders = [];
    Object.values(filteredOrders).forEach(category => {
      allVisibleOrders.push(...category.orders);
    });

    const newOrders = allVisibleOrders.filter(order =>
      !selectedOrders.some(selected => selected.id === order.id)
    );
    setSelectedOrders(prev => [...prev, ...newOrders]);

    if (newOrders.length > 0) {
      message.success(`Added ${newOrders.length} orders to selection.`);
    }
  };

  // Handle date changes with validation
  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date && endDate && date.isAfter(endDate, 'day')) {
      message.warning(language === 'vi'
        ? 'Ngày bắt đầu không thể sau ngày kết thúc'
        : 'Start date cannot be after end date'
      );
      // Auto-adjust end date to be 30 days after start date
      const newEndDate = date.clone().add(30, 'days');
      setEndDate(newEndDate);
      form.setFieldsValue({ endDate: newEndDate });
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (startDate && date && startDate.isAfter(date, 'day')) {
      message.warning(language === 'vi'
        ? 'Ngày kết thúc không thể trước ngày bắt đầu'
        : 'End date cannot be before start date'
      );
      return false;
    }
  };

  // Validate dates before submission
  const validateDates = () => {
    if (!startDate || !endDate) {
      message.error(language === 'vi'
        ? 'Vui lòng chọn ngày bắt đầu và ngày kết thúc'
        : 'Please select both start and end dates'
      );
      return false;
    }

    if (startDate.isAfter(endDate, 'day')) {
      message.error(language === 'vi'
        ? 'Ngày bắt đầu không thể sau ngày kết thúc'
        : 'Start date cannot be after end date'
      );
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (selectedOrders.length === 0) {
      message.warning(language === 'vi' ? 'Vui lòng chọn ít nhất một xét nghiệm!' : 'Please select at least one laboratory order.');
      return;
    }

    if (!user || !user._id) {
      message.error(language === 'vi' ? 'Người dùng chưa đăng nhập hoặc thiếu ID người dùng.' : 'User not logged in or user ID missing.');
      return;
    }

    // Validate dates before submission
    if (!validateDates()) {
      return;
    }

    setLoading(true);
    try {
      // Transform selected orders into the format expected by the order system
      // Use Vietnamese names and descriptions when Vietnamese language is selected
      const orderData = selectedOrders.map(order => ({
        orderType: 'Laboratory',
        orderDetails: {
          testName: language === 'vi' ? order.nameVi : order.testName,
          specimenType: order.specimenType, // Keep in English for system consistency
          labName: '', // Can be filled by user later or set default
          description: language === 'vi' ?
            `${order.nameVi} - ${order.description}` :
            order.description,
          category: order.category, // Keep in English for system consistency
          originalTestName: order.testName, // Keep original for reference
          originalNameEn: order.nameEn,
          originalNameVi: order.nameVi,
          languageUsed: language
        },
        status: 'Active',
        priority: 'Routine',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        patient: patientId,
        orderingProvider: user._id
      }));

      await onOrdersSelected(orderData);

      const successMessage = language === 'vi'
        ? `Đã chọn thành công ${selectedOrders.length} xét nghiệm! Ngày bắt đầu: ${startDate.format('DD/MM/YYYY')}, Ngày kết thúc: ${endDate.format('DD/MM/YYYY')}`
        : `Successfully selected ${selectedOrders.length} laboratory orders! Start: ${startDate.format('DD/MM/YYYY')}, End: ${endDate.format('DD/MM/YYYY')}`;

      message.success(successMessage);
      onClose();
    } catch (error) {
      console.error('Error submitting lab orders:', error);
      const errorMessage = language === 'vi'
        ? 'Không thể gửi đơn xét nghiệm. Vui lòng thử lại.'
        : 'Failed to submit laboratory orders. Please try again.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Render category card
  const renderCategoryCard = (category) => {
    const categoryOrders = category.orders || [];
    const selectedInCategory = selectedOrders.filter(selected => 
      categoryOrders.some(order => order.id === selected.id)
    );
    const isAllSelected = categoryOrders.length > 0 && selectedInCategory.length === categoryOrders.length;
    const isPartiallySelected = selectedInCategory.length > 0 && selectedInCategory.length < categoryOrders.length;

    const nameField = language === 'vi' ? 'nameVi' : 'nameEn';

    return (
      <Card
        key={category.id}
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <span style={{ fontSize: '18px' }}>{category.icon}</span>
            <Text strong style={{ color: category.color }}>
              {category[nameField]}
            </Text>
            <Badge 
              count={selectedInCategory.length} 
              showZero={false}
              style={{ backgroundColor: category.color }}
            />
            <Checkbox
              checked={isAllSelected}
              indeterminate={isPartiallySelected}
              onChange={(e) => handleCategorySelect(categoryOrders, e.target.checked)}
            >
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Select All ({categoryOrders.length})
              </Text>
            </Checkbox>
          </Space>
        }
        extra={
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {selectedInCategory.length}/{categoryOrders.length} selected
          </Text>
        }
      >
        <Row gutter={[8, 8]}>
          {categoryOrders.map(order => {
            const isSelected = selectedOrders.some(selected => selected.id === order.id);
            const orderNameField = language === 'vi' ? 'nameVi' : 'nameEn';
            
            return (
              <Col span={12} key={order.id}>
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => handleOrderSelect(order, e.target.checked)}
                  style={{ width: '100%' }}
                >
                  <Tooltip title={order.description} placement="top">
                    <div style={{ 
                      padding: '4px 8px',
                      border: isSelected ? `1px solid ${category.color}` : '1px solid #d9d9d9',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? `${category.color}10` : 'transparent',
                      fontSize: '12px',
                      lineHeight: '1.4'
                    }}>
                      <Text style={{ 
                        fontSize: '12px',
                        fontWeight: isSelected ? 'bold' : 'normal',
                        color: isSelected ? category.color : 'inherit'
                      }}>
                        {order[orderNameField]}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        {order.specimenType}
                      </Text>
                    </div>
                  </Tooltip>
                </Checkbox>
              </Col>
            );
          })}
        </Row>
      </Card>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <ExperimentOutlined style={{ color: '#1890ff' }} />
          {title}
          <Badge count={selectedOrders.length} showZero={false} />
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      footer={[
        <Button key="clear" onClick={handleClearAll} disabled={selectedOrders.length === 0}>
          <ClearOutlined /> {language === 'vi' ? 'Xóa tất cả' : 'Clear All'}
        </Button>,
        <Button key="selectAll" onClick={handleSelectAll} type="dashed">
          <SelectOutlined /> {language === 'vi' ? 'Chọn tất cả hiển thị' : 'Select All Visible'}
        </Button>,
        <div key="dateInfo" style={{ flex: 1, textAlign: 'center', color: '#666', fontSize: '12px' }}>
          {startDate && endDate && (
            <span>
              <CalendarOutlined /> {language === 'vi' ? 'Thời gian' : 'Duration'}: {startDate.format('DD/MM/YYYY')} - {endDate.format('DD/MM/YYYY')}
              ({endDate.diff(startDate, 'days')} {language === 'vi' ? 'ngày' : 'days'})
            </span>
          )}
        </div>,
        <Button key="cancel" onClick={onClose}>
          {language === 'vi' ? 'Hủy' : 'Cancel'}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={selectedOrders.length === 0 || !startDate || !endDate}
        >
          <CheckCircleOutlined /> {language === 'vi' ? `Thêm ${selectedOrders.length} đơn` : `Add ${selectedOrders.length} Orders`}
        </Button>
      ]}
    >
      <Spin spinning={loading}>
        {/* Search and Language Toggle */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={18}>
            <Search
              placeholder={language === 'vi' ? 'Tìm kiếm xét nghiệm...' : 'Search laboratory tests...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={setSearchTerm}
              allowClear
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col span={6}>
            <Button.Group>
              <Button
                type={language === 'en' ? 'primary' : 'default'}
                onClick={() => setLanguage('en')}
                size="small"
              >
                EN
              </Button>
              <Button
                type={language === 'vi' ? 'primary' : 'default'}
                onClick={() => setLanguage('vi')}
                size="small"
              >
                VI
              </Button>
            </Button.Group>
          </Col>
        </Row>

        {/* Date Selection Controls */}
        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}>
          <Row gutter={16} align="middle">
            <Col span={2}>
              <CalendarOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
            </Col>
            <Col span={10}>
              <Form.Item
                label={language === 'vi' ? 'Ngày bắt đầu' : 'Start Date'}
                style={{ marginBottom: 0 }}
              >
                <MomentDatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  format="DD/MM/YYYY"
                  placeholder={language === 'vi' ? 'Chọn ngày bắt đầu' : 'Select start date'}
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current < moment().startOf('day')}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                label={language === 'vi' ? 'Ngày kết thúc' : 'End Date'}
                style={{ marginBottom: 0 }}
              >
                <MomentDatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  format="DD/MM/YYYY"
                  placeholder={language === 'vi' ? 'Chọn ngày kết thúc' : 'Select end date'}
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && startDate && current < startDate.startOf('day')}
                />
              </Form.Item>
            </Col>
            <Col span={2}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {startDate && endDate ?
                  `${endDate.diff(startDate, 'days')} ${language === 'vi' ? 'ngày' : 'days'}`
                  : ''
                }
              </Text>
            </Col>
          </Row>
        </Card>

        {/* Quick Selection Panels */}
        {!searchTerm && (
          <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
            <Text strong style={{ marginBottom: 8, display: 'block' }}>
              {language === 'vi' ? '🚀 Chọn nhanh bộ xét nghiệm phổ biến:' : '🚀 Quick Select Common Panels:'}
            </Text>
            <Space wrap>
              <Button
                size="small"
                type="dashed"
                onClick={() => {
                  const basicPanel = [
                    ...predefinedLabOrders.hematology.orders.slice(0, 2), // CBC + ESR
                    ...predefinedLabOrders.biochemistry.orders.slice(0, 6), // Basic biochemistry
                    ...predefinedLabOrders.diabetes.orders.slice(0, 2) // Glucose + HbA1c
                  ];
                  const newOrders = basicPanel.filter(order =>
                    !selectedOrders.some(selected => selected.id === order.id)
                  );
                  setSelectedOrders(prev => [...prev, ...newOrders]);
                  message.success(`Added ${newOrders.length} tests from Basic Health Panel`);
                }}
              >
                {language === 'vi' ? 'Bộ sức khỏe cơ bản' : 'Basic Health Panel'}
              </Button>
              <Button
                size="small"
                type="dashed"
                onClick={() => {
                  const cardiacPanel = [
                    ...predefinedLabOrders.cardiovascular.orders,
                    ...predefinedLabOrders.lipidProfile.orders
                  ];
                  const newOrders = cardiacPanel.filter(order =>
                    !selectedOrders.some(selected => selected.id === order.id)
                  );
                  setSelectedOrders(prev => [...prev, ...newOrders]);
                  message.success(`Added ${newOrders.length} tests from Cardiac Panel`);
                }}
              >
                {language === 'vi' ? 'Bộ tim mạch' : 'Cardiac Panel'}
              </Button>
              <Button
                size="small"
                type="dashed"
                onClick={() => {
                  const diabetesPanel = [
                    ...predefinedLabOrders.diabetes.orders,
                    ...predefinedLabOrders.lipidProfile.orders.slice(0, 3)
                  ];
                  const newOrders = diabetesPanel.filter(order =>
                    !selectedOrders.some(selected => selected.id === order.id)
                  );
                  setSelectedOrders(prev => [...prev, ...newOrders]);
                  message.success(`Added ${newOrders.length} tests from Diabetes Panel`);
                }}
              >
                {language === 'vi' ? 'Bộ tiểu đường' : 'Diabetes Panel'}
              </Button>
            </Space>
          </Card>
        )}

        {/* Selected Orders Summary */}
        {selectedOrders.length > 0 && (
          <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}>
            <Space wrap>
              <Text strong style={{ color: '#52c41a' }}>
                <UnorderedListOutlined /> Selected Orders ({selectedOrders.length}):
              </Text>
              {selectedOrders.map(order => (
                <Tag 
                  key={order.id} 
                  closable 
                  onClose={() => handleOrderSelect(order, false)}
                  color="green"
                  style={{ fontSize: '11px' }}
                >
                  {language === 'vi' ? order.nameVi : order.nameEn}
                </Tag>
              ))}
            </Space>
          </Card>
        )}

        {/* Categories */}
        {Object.keys(filteredOrders).length > 0 ? (
          Object.values(filteredOrders).map(category => renderCategoryCard(category))
        ) : (
          <Empty 
            description={
              language === 'vi' 
                ? 'Không tìm thấy xét nghiệm nào phù hợp' 
                : 'No laboratory tests found matching your search'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Spin>
    </Modal>
  );
};

export default PredefinedLabOrdersModal;
